<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Service Prescriber Registration Form"
                document-number="PV01F06"
                version="6.0"
                effective-date="31 Jan 2024"
                review-date="31 Jan 2026"
                supersedes="5.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-5">Denzapine Monitoring Service Prescriber Registration Form</h1>
                <h4 class="mb-8">
                    Please complete all form fields before submission of application. Missing information may result in
                    delays to prescriber registration.
                </h4>
            </v-col>

            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid" :disabled="isDisabled">
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5>I, the undersigned hereby confirm and agree:</h5>
                            <ul>
                                <li>
                                    I am medically qualified to prescribe Denzapine and am aware of all procedures
                                    relating to Denzapine treatment
                                </li>
                                <li>
                                    To participate in the distribution of and treatment with Denzapine within the
                                    Denzapine Monitoring System
                                </li>
                                <li>
                                    Abide by the obligations set out in the Summary of Product Characteristics for all
                                    patients undergoing Denzapine therapy
                                </li>
                            </ul>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Prescriber</h5>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberFirstName"
                                label="First Name:"
                                mandatory
                                :rules="validationRules.prescriberFirstName"
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberLastName"
                                label="Last Name:"
                                :rules="validationRules.prescriberLastName"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberPhone"
                                label="Phone"
                                :rules="validationRules.prescriberPhone"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberEmail"
                                label="Email Address:"
                                :rules="validationRules.prescriberEmail"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberAddress"
                                label="Address:"
                                :rules="validationRules.prescriberAddress"
                                mandatory
                            ></b-input>
                        </v-col>

                        <v-col cols="12" sm="8" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.prescriberMobilePhoneNumber"
                                label="Mobile Phone Number / Out of house contact"
                                :rules="validationRules.prescriberMobilePhoneNumber"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.gmcNumber"
                                label="GMC Number:"
                                :rules="validationRules.gmcNumber"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.gmcSpecialistRegister"
                                label="Registered on the GMC Specialist Register:"
                                mandatory
                                :rules="validationRules.gmcSpecialistRegister"
                                :radio-data="radioDataBinary"
                                connected-field="prescriberRegistrationForm.healthcareProfessional"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                auto-grow
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.healthcareProfessional"
                                label="If not registered on the GMC Specialist Register, please confirm below that you are the most appropriate healthcare professional to be registered as the responsible consultant for Denzapine patients."
                                :rules="
                                    isGmcSpecialistRegisterAdditionalFieldDisabled
                                        ? validationRules.healthcareProfessional
                                        : []
                                "
                                :mandatory="isGmcSpecialistRegisterAdditionalFieldDisabled"
                                :disabled="!isGmcSpecialistRegisterAdditionalFieldDisabled"
                            ></b-textarea>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <h5 class="patient-form-title mt-5">Signature</h5>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <b-date-picker-store
                                hide-details
                                to-mutate="secure_sign_forms::prescriberRegistrationForm.date"
                                label="Date"
                                :rules="validationRules.date"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>
                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>
                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                    </v-row>
                    <s-s-form-footer>
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300033 Date of prep: Jan 2024</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormHeader from './partials/SSFormHeader.vue';
import SSFormFooter from './partials/SSFormFooter.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormFooter, SSFormHeader },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                prescriberFirstName: [(v) => !!v || t('users::validation.field.required')],
                prescriberLastName: [(v) => !!v || t('users::validation.field.required')],
                prescriberAddress: [(v) => !!v || t('users::validation.field.required')],
                prescriberPhone: [(v) => !!v || t('users::validation.field.required')],
                prescriberEmail: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                prescriberMobilePhoneNumber: [(v) => !!v || t('users::validation.field.required')],
                gmcNumber: [(v) => !!v || t('users::validation.field.required')],
                gmcSpecialistRegister: [(v) => !!v || t('users::validation.field.required')],
                healthcareProfessional: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
            },
            radioDataBinary: [
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
            ],
            isFormValid: false,
            formType: 'prescriber-registration-form',
            form: 'prescriberRegistrationForm',
        };
    },
    computed: {
        isGmcSpecialistRegisterAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.prescriberRegistrationForm.gmcSpecialistRegister === 'No';
            },
        },
    },
    created() {
        this.getFormData();
    },
};
</script>

<style scoped></style>
